import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';

import { rejectValue, flatFundRaisingProduct } from 'redux/helpers';
import { priceForAPI } from 'utils/price';

import { selectStore } from '../javelinStore';
import {
  GET_PRODUCT_LIST,
  GET_PRODUCT_BY_ID,
  POST_PRODUCT,
  PATCH_PRODUCT,
  POST_PRODUCT_SHARE,
} from 'services/api';
import { FUNDRAISER_PRODUCT } from 'services/api/types';
import {
  FUNDRAISER,
  FUNDRAISER_ALL_ACTION,
  FUNDRAISER_CREATE_ACTION,
  FUNDRAISER_UPDATE_ACTION,
  FUNDRAISER_LIVE_ACTION,
  FUNDRAISER_BY_ID,
  FUNDRAISER_TXTPAY_ACTION,
} from './types';
import { showErrorMessage, showSuccessMessage } from '../notification';

const fundraiserAdapter = createEntityAdapter({
  sortComparer: (a, b) => b.id.localeCompare(a.id),
});

export const initialState = fundraiserAdapter.getInitialState({
  loading: false,
  pagination: {
    totalPages: 1,
    currentPage: 1,
  },
  hasLiveFundraisers: false,
  liveFundraisers: {},
});

export const getFundraiserList = createAsyncThunk(
  FUNDRAISER_ALL_ACTION,
  async (
    { page, shouldFetchLive },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const storeId = getState().javelinStore.current;
      const _page = page || getState().fundraiser.pagination.currentPage || 1;
      const { data, meta } = await GET_PRODUCT_LIST({
        type: FUNDRAISER_PRODUCT,
        page: _page,
      });

      if (!data.length)
        return { data: [], pagination: meta.pagination, storeId };

      if (shouldFetchLive) {
        dispatch(getLiveFundraisers());
      }
      const _data = data.map((item) => {
        const clonedObj = { ...item };
        delete clonedObj.productable;

        return {
          ...clonedObj,
          page: meta.pagination.current_page || 1,
          ...flatFundRaisingProduct(item),
        };
      });
      return { data: _data, pagination: meta.pagination, storeId };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getLiveFundraisers = createAsyncThunk(
  FUNDRAISER_LIVE_ACTION,
  async (_, { rejectWithValue, getState }) => {
    try {
      const {
        data,
        meta: {
          pagination: { total = 0 },
        },
      } = await GET_PRODUCT_LIST({
        on_sale: true,
        type: FUNDRAISER_PRODUCT,
        per_page: 3,
      });
      const _data = data.map((item) => {
        const clonedObj = { ...item };
        delete clonedObj.productable;

        return {
          ...clonedObj,
          ...flatFundRaisingProduct(item),
        };
      });
      return {
        data: _data,
        hasLiveFundraisers: !!total,
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getFundraiserById = createAsyncThunk(
  FUNDRAISER_BY_ID,
  async (id, { rejectWithValue, getState }) => {
    try {
      const { data } = await GET_PRODUCT_BY_ID(id);
      const clonedObj = { ...data };
      delete clonedObj.productable;

      return {
        ...clonedObj,
        ...flatFundRaisingProduct(data),
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const createFundraiser = createAsyncThunk(
  FUNDRAISER_CREATE_ACTION,
  async (payload, { rejectWithValue, getState, dispatch }) => {
    const requestBody = {
      ...payload,
      type: FUNDRAISER_PRODUCT,
      store_id: getState().javelinStore.current,
      goal: priceForAPI(payload.goal),
      currency: 'EUR',
      on_sale: true,
    };

    try {
      await POST_PRODUCT(requestBody);
      dispatch(getFundraiserList({}));
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const txtpayShare = createAsyncThunk(
  FUNDRAISER_TXTPAY_ACTION,
  async (payload, { rejectWithValue, getState, dispatch }) => {
    try {
      await POST_PRODUCT_SHARE(payload);
      dispatch(showSuccessMessage('Fundraiser shared successfully!'));
    } catch (error) {
      dispatch(
        showErrorMessage('Failed to share fundraiser! Please try again later.')
      );
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const editFundraiser = createAsyncThunk(
  FUNDRAISER_UPDATE_ACTION,
  async (payload, { rejectWithValue, getState }) => {
    let requestBody = payload;

    if (payload.goal) {
      requestBody.goal = priceForAPI(payload.goal);
    }

    try {
      const { data } = await PATCH_PRODUCT(requestBody);
      const clonedObj = { ...data };
      delete clonedObj.productable;

      return {
        id: data.id,
        changes: {
          ...clonedObj,
          ...flatFundRaisingProduct(data),
        },
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const fundraiserSlice = createSlice({
  name: FUNDRAISER,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFundraiserList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFundraiserList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getFundraiserList.fulfilled, (state, { payload, meta }) => {
        state.loading = false;
        if (!!payload.storeId) {
          state.pagination = {
            totalPages: payload.pagination.total_pages || 1,
            currentPage: payload.pagination.current_page || 1,
          };
        }
        fundraiserAdapter.setAll(state, payload.data);
      })
      .addCase(getLiveFundraisers.fulfilled, (state, { payload, meta }) => {
        state.hasLiveFundraisers = payload.hasLiveFundraisers;
        state.liveFundraisers = payload.data;
      })
      .addCase(selectStore.fulfilled, (state, { payload }) => {
        if (payload.id) {
          state.pagination = {
            currentPage: 1,
            totalPages: 1,
          };
        }
      })
      .addCase(editFundraiser.fulfilled, fundraiserAdapter.updateOne)
      .addCase(getFundraiserById.fulfilled, fundraiserAdapter.upsertOne)
      .addDefaultCase((state) => state);
  },
});

export const fundraiserSelectors = fundraiserAdapter.getSelectors(
  (state) => state.fundraiser
);

export default fundraiserSlice.reducer;
